import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import {
  IExamReportCardResponse,
  IParentExamListResponse,
} from '../../interface/reportCard.interface';
import {
  IMyStudentIdentity,
  IMyStudentIdentityResponse,
  IStudentExamDetailResponse,
  IStudentPortalExamState,
} from '../../interface/studentPortalExam.interface';
import {
  fetchMyStudentIdentity,
  fetchStudentExamDetail,
  fetchStudentExamList,
  fetchStudentReportCard,
} from '../action/studentPortalExam.action';

const initialState: IStudentPortalExamState = {
  identity: null,
  identityLoading: false,
  identityError: null,
  exams: [],
  listLoading: false,
  listError: null,
  detail: null,
  detailLoading: false,
  detailError: null,
  reportCard: null,
  reportCardLoading: false,
  reportCardError: null,
  downloadLoading: false,
};

const studentPortalExamSlice = createSlice({
  name: 'studentPortalExam',
  initialState,
  reducers: {
    // Pages dispatch this on mount for a clean state.
    resetStudentPortalExamState: () => initialState,
    clearStudentExamDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
    clearStudentReportCard: (state) => {
      state.reportCard = null;
      state.reportCardError = null;
    },
    setStudentReportCardDownloading: (state, action: PayloadAction<boolean>) => {
      state.downloadLoading = action.payload;
    },
    /**
     * CVA-4197 "one flow, two audiences": a guardian has no student identity
     * of their own to resolve via GET /v2/me/student (that call is for a
     * STUDENT'S own login only, and 404s for a guardian). A guardian already
     * has an explicitly selected child — the same sidebar selector every
     * other guardian-facing feature reads (`auth.slice.selectedStudent`) —
     * so this sets `identity` directly from that, with no network call.
     */
    setIdentityFromGuardian: (state, action: PayloadAction<IMyStudentIdentity>) => {
      state.identity = action.payload;
      state.identityLoading = false;
      state.identityError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyStudentIdentity.pending, (state) => {
        state.identityLoading = true;
        state.identityError = null;
      })
      .addCase(
        fetchMyStudentIdentity.fulfilled,
        (state, action: PayloadAction<IMyStudentIdentityResponse>) => {
          state.identityLoading = false;
          state.identity = action.payload.data;
          state.identityError = null;
        },
      )
      .addCase(fetchMyStudentIdentity.rejected, (state, action) => {
        state.identityLoading = false;
        state.identity = null;
        state.identityError =
          action.payload?.message ||
          tr('student_portal_exam.error_identity', 'Failed to load your student profile.');
      })

      .addCase(fetchStudentExamList.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(
        fetchStudentExamList.fulfilled,
        (state, action: PayloadAction<IParentExamListResponse>) => {
          state.listLoading = false;
          state.exams = action.payload.data.exams;
          state.listError = null;
        },
      )
      .addCase(fetchStudentExamList.rejected, (state, action) => {
        state.listLoading = false;
        state.exams = [];
        state.listError =
          action.payload?.message || tr('student_portal_exam.error_list', 'Failed to fetch exams.');
      })

      .addCase(fetchStudentExamDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(
        fetchStudentExamDetail.fulfilled,
        (state, action: PayloadAction<IStudentExamDetailResponse>) => {
          state.detailLoading = false;
          state.detail = action.payload.data;
          state.detailError = null;
        },
      )
      .addCase(fetchStudentExamDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detail = null;
        state.detailError =
          action.payload?.message ||
          tr('student_portal_exam.error_detail', 'Failed to fetch the exam.');
      })

      .addCase(fetchStudentReportCard.pending, (state) => {
        state.reportCardLoading = true;
        state.reportCardError = null;
      })
      .addCase(
        fetchStudentReportCard.fulfilled,
        (state, action: PayloadAction<IExamReportCardResponse>) => {
          state.reportCardLoading = false;
          state.reportCard = action.payload.data;
          state.reportCardError = null;
        },
      )
      .addCase(fetchStudentReportCard.rejected, (state, action) => {
        state.reportCardLoading = false;
        state.reportCard = null;
        state.reportCardError =
          action.payload?.message ||
          tr('student_portal_exam.error_report_card', 'Failed to fetch the report card.');
      });
  },
});

export const {
  resetStudentPortalExamState,
  clearStudentExamDetail,
  clearStudentReportCard,
  setStudentReportCardDownloading,
  setIdentityFromGuardian,
} = studentPortalExamSlice.actions;
export default studentPortalExamSlice.reducer;
